import React, { useCallback, useEffect, useRef, useState } from 'react';
import aulaArchivosService from '../../services/AulaArchivosService';
import TableExportActions from './TableExportActions';
import './AulaArchivosPanel.css';

const AulaArchivosPanel = ({ aulaId, aulaNombre, canUpload = true, service = aulaArchivosService, resourceLabel = 'aula' }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const tableRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    if (!aulaId) return;

    setLoading(true);
    setError('');
    try {
      const res = await service.list(aulaId);
      const items = res?.items || res?.Items || res || [];
      setFiles(Array.isArray(items) ? items : []);
    } catch (e) {
      setError(e.message || 'No pudimos cargar los archivos.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [aulaId, service]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      await service.upload(aulaId, file);
      await fetchFiles();
    } catch (e) {
      setError(e.message || 'No pudimos subir el archivo.');
    } finally {
      setUploading(false);
      event.target.value = null;
    }
  };

  const handleDelete = async (archivoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;

    try {
      await service.delete(aulaId, archivoId);
      await fetchFiles();
    } catch (e) {
      setError(e.message || 'No pudimos eliminar el archivo.');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (archivoId, filename) => {
    try {
      const blob = await service.downloadFile(aulaId, archivoId);
      downloadBlob(blob, filename || 'archivo');
    } catch (e) {
      setError(e.message || 'No pudimos descargar el archivo.');
    }
  };

  const handleDownloadZip = async () => {
    try {
      const blob = await service.downloadZip(aulaId);
      downloadBlob(blob, `archivos-${aulaNombre || 'aula'}.zip`);
    } catch (e) {
      setError(e.message || 'No pudimos descargar el ZIP.');
    }
  };

  return (
    <div className="aula-files-panel">
      <div className="aula-files-toolbar">
        <TableExportActions
          tableRef={tableRef}
          fileName={`archivos-${aulaNombre || 'aula'}`}
          title={`Archivos de ${aulaNombre || 'aula'}`}
          compact
          disabled={files.length === 0}
        />
        <div className="aula-files-toolbar-actions">
          {canUpload && (
            <label className="btn btn-primary">
              {uploading ? 'Subiendo...' : 'Subir archivo'}
              <input type="file" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
            </label>
          )}
          <button className="btn btn-secondary" onClick={handleDownloadZip} disabled={loading || files.length === 0}>
            Descargar todo (.zip)
          </button>
        </div>
      </div>

      {error && (
        <div className="aula-files-alert">
          <i className="fas fa-exclamation-triangle" />
          <span>{error}</span>
        </div>
      )}

      <div className="aula-files-content">
        {loading ? (
          <div className="aula-files-empty">
            <i className="fas fa-spinner fa-spin" />
            <p>Cargando archivos...</p>
          </div>
        ) : files.length === 0 ? (
          <div className="aula-files-empty">
            <i className="fas fa-folder-open" />
            <p>No hay archivos cargados en esta {resourceLabel}.</p>
          </div>
        ) : (
          <div className="aula-files-table-wrapper">
            <table ref={tableRef} className="data-table aula-files-table">
              <thead>
                <tr>
                  <th>Archivo</th>
                  <th>Fecha</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id || file.Id}>
                    <td>{file.nombreArchivo || file.NombreArchivo}</td>
                    <td>{file.fechaAlta ? new Date(file.fechaAlta || file.FechaAlta).toLocaleString() : '—'}</td>
                    <td className="text-right">
                      <div className="aula-files-actions">
                        <button
                          className="btn btn-outline"
                          onClick={() => handleDownload(file.id || file.Id, file.nombreArchivo || file.NombreArchivo)}
                        >
                          <i className="fas fa-download" />
                        </button>
                        {canUpload && (
                          <button className="btn btn-outline danger" onClick={() => handleDelete(file.id || file.Id)}>
                            <i className="fas fa-trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AulaArchivosPanel;
